import React, { useMemo } from 'react';
import { ModalTitle, useModal } from '@topconagriculture/modal';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { DeviationBarchart } from './DeviationBarchart';
import { ToleranceAreaChart } from './ToleranceAreaChart';
import { useIngredientAlertsMutation } from '../../hooks/useIngredientAlertsMutation';
import { ActionIcon, Button, ButtonVariants, FontIcon, Text } from '@topconagriculture/react-components';
import { WidgetFooter } from '../../../WidgetFooter';
import { WidgetSize } from '../../../../widget.types';
import { Loader } from '@topconagriculture/loader';
import { deviationGeneralAlertsModalName } from '../../DeviationWidget';
import { modalTitleMap } from '../DeviationWidgetLModal';
import { useDashboardDeviationSubscription } from '../../hooks/useDashboardDeviationSubscription';
import { useDeviationAlertsSubscription } from '../../hooks/useDeviationAlertsSubscription';
const useStyles = makeStyles((theme) => ({
    modalContainer: {
        width: 1024,
        height: 615,
        paddingTop: 15,
        position: 'relative',
    },
    narrowButton: {
        minWidth: 80,
        width: 'fit-content',
        minHeight: 20,
        paddingTop: 3,
        paddingBottom: 3,
        display: 'block',
        margin: '10px auto',
    },
    arrow: {
        position: 'absolute',
        left: 40,
        top: 5,
        zIndex: 99,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
    },
    title: {
        fontSize: theme.font.size.big,
        fontWeight: theme.font.weight.light,
        padding: '20px 0px 0px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    closeIcon: {
        width: 'fit-content',
    },
}));
export const createChartData = (data) => {
    if (!data) {
        return [];
    }
    const dayAlertMap = data.items.reduce((acc, curr) => {
        const date = curr.dateTime.split('T')[0];
        const itemData = { id: curr.id, deviation: curr.deviation, date: curr.dateTime, rationId: curr.rationId };
        if (acc[date]) {
            const key = Object.keys(acc[date]).length - 1;
            acc[date] = {
                ...acc[date],
                [key]: { ...itemData, deviation: curr.deviation + 0.0001 * key },
            };
        }
        else {
            acc[date] = {
                0: itemData,
                tolerance: [-data.tolerance, data.tolerance],
            };
        }
        return acc;
    }, {});
    return Object.entries(dayAlertMap).map(([date, alerts]) => ({ ...alerts, date }));
};
export const DeviationChartModal = () => {
    var _a, _b;
    const { modalParams, closeModal, dispatchOpenModal } = useModal();
    const { data, isLoading } = useDashboardDeviationSubscription(modalParams.id, modalParams.widget.type);
    const { refetch: refetchAlerts } = useDeviationAlertsSubscription(modalParams.widget.type);
    const chartData = useMemo(() => createChartData(data), [data]);
    const domain = useMemo(() => (data ? [-(data === null || data === void 0 ? void 0 : data.tolerance) * 5, (data === null || data === void 0 ? void 0 : data.tolerance) * 5] : undefined), [data]);
    const { mutate: removeAlerts } = useIngredientAlertsMutation(modalParams.widget.type);
    const widgetSize = (_b = (_a = modalParams === null || modalParams === void 0 ? void 0 : modalParams.widget) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.size;
    const clearAlerts = async () => {
        await removeAlerts(data.itemId);
        returnBack();
    };
    const returnBack = () => {
        closeModal();
        switch (widgetSize) {
            case WidgetSize.XS:
            case WidgetSize.S:
                dispatchOpenModal({
                    modalName: deviationGeneralAlertsModalName,
                    modalParams: { link: modalParams.link, widget: modalParams.widget },
                    modalOptions: {
                        modalDialogProps: {
                            maxWidth: 'xl',
                        },
                    },
                });
                break;
            default:
                break;
        }
    };
    const { t } = useTranslation();
    const classes = useStyles();
    const close = () => {
        closeModal();
        refetchAlerts();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ModalTitle, { classes: { title: classes.title } },
            React.createElement(Text, { size: 18 }, t(modalTitleMap[modalParams.widget.type])),
            React.createElement(ActionIcon, { className: classes.closeIcon, iconName: "close", iconSize: 16, onClick: close })),
        React.createElement("div", { className: classes.modalContainer },
            React.createElement("button", { onClick: returnBack, className: classes.arrow },
                React.createElement(FontIcon, { iconName: "arrow-left", size: 40, color: "#0e69b8" })),
            isLoading ? (React.createElement(Loader, null)) : (React.createElement(React.Fragment, null,
                React.createElement(ToleranceAreaChart, { chartData: chartData, domain: domain }),
                React.createElement(DeviationBarchart, { chartData: chartData, domain: domain, title: data === null || data === void 0 ? void 0 : data.name }))),
            React.createElement(WidgetFooter, { link: `${modalParams.link}?allExpanded=true`, viewMoreButtonIsDisabled: false, center: React.createElement(Button, { className: classes.narrowButton, role: ButtonVariants.primary, label: t('widget:clear_alerts'), onClick: clearAlerts }) }))));
};
//# sourceMappingURL=DeviationChartModal.js.map